import React, { Component, Fragment } from 'react';
import { Card } from '@philips-internal/oswald-ui-components';
import { SelectionType } from '@philips-internal/oswald-ui-constants';
import { classNames } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as IntenseSimplePainIcon } from '../../assets/images/global/face2.4.svg';
import { ReactComponent as MildSimplePainIcon } from '../../assets/images/global/face2.2.svg';
import { ReactComponent as ModerateSimplePainIcon } from '../../assets/images/global/face2.3.svg';
import { ReactComponent as PainlessSimpleIcon } from '../../assets/images/global/face2.1.svg';
import { ReactComponent as UnbearableSimplePainIcon } from '../../assets/images/global/face2.5.svg';
import { ReactComponent as IntensePainIcon } from '../../assets/images/global/IntensePain.svg';
import { ReactComponent as MildPainIcon } from '../../assets/images/global/MildPain.svg';
import { ReactComponent as ModeratePainIcon } from '../../assets/images/global/ModeratePain.svg';
import { ReactComponent as PainlessIcon } from '../../assets/images/global/Painless.svg';
import { ReactComponent as UnbearablePainIcon } from '../../assets/images/global/UnbearablePain.svg';
import { VISUAL_REP_COMPLEX_DATA, VISUAL_REP_LIGHT_DATA, VISUAL_REP_SIMPLE_DATA } from './pain-scale-data';
import PainScaleReference from './pain-scale-reference';
import { PainScaleIconType, PainScaleType } from './pain-scale-type';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
class PainScaleVisual extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            selectedValue: undefined,
        };
    }
    renderIcon(key) {
        const icon = {
            [PainScaleIconType.PAINLESS]: React.createElement(PainlessIcon, { className: "c-pain-scale__option-icon" }),
            [PainScaleIconType.MILD_PAIN]: React.createElement(MildPainIcon, { className: "c-pain-scale__option-icon" }),
            [PainScaleIconType.MODERATE_PAIN]: React.createElement(ModeratePainIcon, { className: "c-pain-scale__option-icon" }),
            [PainScaleIconType.INTENSE_PAIN]: React.createElement(IntensePainIcon, { className: "c-pain-scale__option-icon" }),
            [PainScaleIconType.UNBEARABLE_PAIN]: React.createElement(UnbearablePainIcon, { className: "c-pain-scale__option-icon" }),
        }[key];
        return icon;
    }
    renderSimpleIcon(key) {
        const icon = {
            [PainScaleIconType.PAINLESS]: React.createElement(PainlessSimpleIcon, { className: "c-pain-scale__option-icon" }),
            [PainScaleIconType.MILD_PAIN]: React.createElement(MildSimplePainIcon, { className: "c-pain-scale__option-icon" }),
            [PainScaleIconType.MODERATE_PAIN]: React.createElement(ModerateSimplePainIcon, { className: "c-pain-scale__option-icon" }),
            [PainScaleIconType.INTENSE_PAIN]: React.createElement(IntenseSimplePainIcon, { className: "c-pain-scale__option-icon" }),
            [PainScaleIconType.UNBEARABLE_PAIN]: React.createElement(UnbearableSimplePainIcon, { className: "c-pain-scale__option-icon" }),
        }[key];
        return icon;
    }
    renderComplexRepresentation() {
        const scaleData = VISUAL_REP_COMPLEX_DATA;
        const groups = [...new Set(scaleData.options.map((option) => option.group))];
        const complexScaleClass = classNames({
            'c-pain-scale__horizontal-scale': Boolean(scaleData.horizontalView),
        });
        return (React.createElement(Fragment, null,
            React.createElement("div", { className: complexScaleClass }, groups.map((group, index) => {
                const options = scaleData.options.filter((el) => el.group === group);
                const baseElement = options[0];
                const isEven = index % 2 === 0;
                const className = classNames({
                    'c-pain-scale__complex-group': true,
                    'c-pain-scale__complex-group--even': Boolean(isEven),
                });
                return (React.createElement("div", { className: className, key: group },
                    React.createElement("div", { className: "c-pain-scale__complex-group-header" },
                        this.renderIcon(baseElement.icon),
                        baseElement.groupLabel
                            ? React.createElement("label", { className: "c-pain-scale__complex-group-label" }, MessageService.translate(baseElement.groupLabel))
                            : null),
                    React.createElement("div", { className: "c-pain-scale__complex-group-options" }, this.renderComplexOptions(options, scaleData))));
            })),
            scaleData.references
                ? React.createElement(PainScaleReference, { className: "c-pain-scale__reference-link-complex", referenceText: scaleData.references })
                : null));
    }
    renderComplexOptions(options, data) {
        const { selectedValue } = this.state;
        const { value } = this.props;
        return (React.createElement(Fragment, null, options.map((option, index) => {
            const selected = option.value === (selectedValue ?? value);
            const optionClass = classNames({
                'c-pain-scale__option-complex': true,
                'c-pain-scale__option-complex-selected': selected,
            });
            const labelProps = {
                color: data.showColor ? option.color : 'unset',
                value: option.value
            };
            return (React.createElement("div", { className: optionClass, key: index, onClick: () => this.onSelect(option.value) }, this.renderLabel(labelProps)));
        })));
    }
    renderSimpleRepresentation() {
        const scaleData = VISUAL_REP_SIMPLE_DATA;
        const simpleScaleClass = classNames({
            'c-pain-scale__horizontal-scale': Boolean(scaleData.horizontalView),
            'c-pain-scale__simple-scale-wrapper': true,
        });
        return (React.createElement(Fragment, null,
            React.createElement("div", { className: simpleScaleClass }, this.renderSimpleOptions(scaleData)),
            scaleData.references ?
                React.createElement(PainScaleReference, { className: "c-pain-scale__reference-link-simple", referenceText: scaleData.references })
                : null));
    }
    renderLightRepresentation() {
        const scaleData = VISUAL_REP_LIGHT_DATA;
        const simpleScaleClass = classNames({
            'c-pain-scale__horizontal-scale': Boolean(scaleData.horizontalView),
            'c-pain-scale__simple-scale-wrapper': true,
        });
        return (React.createElement(Fragment, null,
            React.createElement("div", { className: simpleScaleClass }, this.renderSimpleOptions(scaleData)),
            scaleData.references ?
                React.createElement(PainScaleReference, { className: "c-pain-scale__reference-link-simple", referenceText: scaleData.references })
                : null));
    }
    renderSimpleOptions(data) {
        const { value } = this.props;
        const { selectedValue } = this.state;
        return (React.createElement(Fragment, null, data.options.map((option) => {
            const selected = option.value === (selectedValue ?? value);
            const optionClass = classNames({
                'c-pain-scale__option': true,
                'c-pain-scale__option-selected': selected,
            });
            const labelProps = {
                color: data.showColor ? option.color : 'unset',
                value: option.value,
            };
            return (React.createElement(Card, { className: "c-pain-scale__card-option", key: `pain-scale-${option.value}`, onSelect: () => this.onSelect(option.value), padding: "0", selectable: true, selected: selected, selectionType: SelectionType.SINGLE, uuid: `pain-scale-${option.value}-uuid` },
                React.createElement("div", { className: optionClass },
                    React.createElement("div", { className: "c-pain-scale__option-icon-wrapper" }, data.showValue ? this.renderSimpleIcon(option.icon) : this.renderIcon(option.icon)),
                    data.showValue ? this.renderLabel(labelProps) : null)));
        })));
    }
    renderLabel({ color, value }) {
        return (React.createElement("label", { className: "c-pain-scale__option-label", style: { backgroundColor: color } }, value));
    }
    renderScale() {
        const { scaleType } = this.props;
        switch (scaleType) {
            case PainScaleType.VISUAL_REP_SIMPLE:
                return this.renderSimpleRepresentation();
            case PainScaleType.VISUAL_REP_LIGHT:
                return this.renderLightRepresentation();
            default:
                return this.renderComplexRepresentation();
        }
    }
    onSelect(value) {
        this.setState({ selectedValue: value });
        const { onSave } = this.props;
        if (!isNaN(value)) {
            onSave(value);
        }
    }
    render() {
        return (this.renderScale());
    }
}
export default PainScaleVisual;
//# sourceMappingURL=pain-scale-visual.js.map